import { Injectable, OnDestroy, inject } from '@angular/core';
import { interval, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PreferencesService } from '../../services/preferences/preferences.service';
import { BrightcovePlayerRestService } from './brightcove-player-rest.service';
import { BrightcovePlayer, BrightcoveEvent } from './brightcove-player.domain';

/**
 * This Service is used when brightcove player is loaded on page and do one thing:
 * keep the session alive while user watches a video by trigger backend before session time out
 */
@Injectable()
export class BrightcovePlayerService implements OnDestroy {
  private readonly preferencesService = inject(PreferencesService);
  private readonly brightcovePlayerRestService = inject(BrightcovePlayerRestService);

  private sessionTimeout: number;
  private sessionTimeoutSubscription: Subscription = Subscription.EMPTY;
  private intervalSubscription: Subscription = Subscription.EMPTY;
  private preferenceSubscription: Subscription = Subscription.EMPTY;

  ngOnDestroy(): void {
    this.stopKeepSessionAlive();
  }

  keepSessionAliveOnPlay(playerRef: BrightcovePlayer): void {
    this.preferenceSubscription = this.getUserSessionTimeout().subscribe((): void => {
      playerRef.ready((): void => {
        playerRef.on(BrightcoveEvent.PLAY, (): void => {
          this.keepSessionAlive();
        });
        playerRef.on(BrightcoveEvent.PAUSE, (): void => {
          this.triggerSessionAlive();
          this.stopKeepSessionAlive();
        });
      });
    });
  }

  private keepSessionAlive(): void {
    this.stopKeepSessionAlive();
    this.triggerSessionAlive();
    this.intervalSubscription = interval(this.sessionTimeout / 2).subscribe((): void => {
      this.triggerSessionAlive();
    });
  }

  private getUserSessionTimeout(): Observable<void> {
    return this.preferencesService.getUserPreference<number>('SESSION_TIMEOUT').pipe(map((result: number): void => {
      this.sessionTimeout = result * 1000;
    }));
  }

  private triggerSessionAlive(): void {
    this.sessionTimeoutSubscription = this.brightcovePlayerRestService.triggerSessionAlive().subscribe();
  }

  private stopKeepSessionAlive(): void {
    this.sessionTimeoutSubscription.unsubscribe();
    this.intervalSubscription.unsubscribe();
    this.preferenceSubscription.unsubscribe();
  }
}
